import { useLayoutEffect, useEffect, type RefObject } from 'react';
import hljs from 'highlight.js';

hljs.configure({ ignoreUnescapedHTML: true });

const AUTO_DETECT_LANGUAGES = [
  'json', 'yaml', 'xml', 'html', 'css', 'scss',
  'javascript', 'typescript', 'jsx', 'tsx',
  'python', 'java', 'csharp', 'cpp', 'c', 'go', 'rust', 'kotlin', 'swift',
  'php', 'ruby', 'shell', 'bash', 'powershell',
  'sql', 'graphql', 'markdown', 'dockerfile', 'ini', 'diff',
];

function sniffJson(text: string): 'json' | null {
  const trimmed = text.trim();
  if (!/^[{[]/.test(trimmed) || !/[}\]]$/.test(trimmed)) return null;
  try {
    JSON.parse(trimmed);
    return 'json';
  } catch {
    return null;
  }
}

const COPY_ICON = `<svg width="18" height="18" viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M5.60417 17.2291V2.47913H17.8542V17.2291H5.60417ZM1.3125 21.5208V6.77079H3.9375V18.8958H13.5625V21.5208H1.3125Z" fill="currentColor"/></svg>`;
const CHECK_ICON = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`;

function isDarkTheme(): boolean {
  return document.documentElement.classList.contains('dark');
}

function setWrapperTheme(wrapper: HTMLElement): void {
  wrapper.dataset.theme = isDarkTheme() ? 'dark' : 'light';
}

function buildCodeBlock(pre: HTMLElement): void {
  if (pre.closest('.code-block-wrapper')) return;

  // Contentstack RTE outputs <pre>content</pre> without a <code> child.
  let codeEl = pre.querySelector<HTMLElement>(':scope > code');
  if (!codeEl) {
    codeEl = document.createElement('code');
    codeEl.innerHTML = pre.innerHTML;
    pre.innerHTML = '';
    pre.appendChild(codeEl);
  }

  // RTE typically attaches data-language to the <pre>; forward it to the <code>
  // as a real language-* class so hljs.highlightElement honors it.
  const preLang = pre.dataset.language;
  if (preLang && !codeEl.className.includes('language-')) {
    codeEl.classList.add(`language-${preLang}`);
  }

  // If no language is set, sniff JSON first (hljs.highlightAuto mis-classifies
  // JSON as CSS / smalltalk because of overlapping {key: value} grammar), then
  // fall back to autodetect against a curated subset.
  if (!codeEl.dataset.language && !codeEl.className.includes('language-')) {
    const text = codeEl.textContent ?? '';
    const detectedLang = sniffJson(text) ?? hljs.highlightAuto(text, AUTO_DETECT_LANGUAGES).language;
    if (detectedLang) codeEl.classList.add(`language-${detectedLang}`);
  }
  hljs.highlightElement(codeEl);

  // Wrap each line in a block span so wrapped lines indent to the same level.
  // Line numbers are rendered via CSS counter on .code-line — no separate gutter,
  // so a wrapped line keeps a single number aligned with its first visual row.
  const htmlLines = codeEl.innerHTML.split('\n');
  if (htmlLines[htmlLines.length - 1] === '') htmlLines.pop();
  codeEl.innerHTML = htmlLines.map(line => {
    const textOnly = line.replace(/<[^>]+>/g, '');
    const indent = (textOnly.match(/^([ \t]*)/) ?? ['', ''])[1].replace(/\t/g, '    ').length + 3;
    const style = indent > 0 ? ` style="padding-left:${indent}ch;text-indent:-${indent}ch"` : '';
    return `<span class="code-line"${style}>${line}</span>`;
  }).join('');

  // Header with copy button
  const copyBtn = document.createElement('button');
  copyBtn.type = 'button';
  copyBtn.className = 'code-block-copy';
  copyBtn.setAttribute('aria-label', 'Copy code');
  copyBtn.title = 'Copy code';
  copyBtn.innerHTML = COPY_ICON;

  let copyTimeout: ReturnType<typeof setTimeout>;
  copyBtn.addEventListener('click', () => {
    navigator.clipboard
      .writeText(codeEl!.textContent ?? '')
      .then(() => {
        copyBtn.innerHTML = CHECK_ICON;
        copyBtn.title = 'Copied!';
        clearTimeout(copyTimeout);
        copyTimeout = setTimeout(() => { copyBtn.innerHTML = COPY_ICON; copyBtn.title = 'Copy code'; }, 2000);
      })
      .catch(() => {});
  });

  const header = document.createElement('div');
  header.className = 'code-block-header';
  header.appendChild(copyBtn);

  const wrapper = document.createElement('div');
  wrapper.className = 'code-block-wrapper';
  setWrapperTheme(wrapper);

  pre.parentNode?.insertBefore(wrapper, pre);
  wrapper.appendChild(header);
  wrapper.appendChild(pre);
}

export function useCodeHighlight(ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const container = ref.current;
    if (!container) return;
    container.querySelectorAll<HTMLElement>('pre').forEach(buildCodeBlock);
    container.querySelectorAll<HTMLElement>('.code-block-wrapper').forEach(setWrapperTheme);
  });

  // Keep data-theme in sync when user toggles light/dark.
  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    const observer = new MutationObserver(() => {
      container.querySelectorAll<HTMLElement>('.code-block-wrapper').forEach(setWrapperTheme);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
